import React from 'react';
import {
    Avatar,
    Box,
    Card,
    CardContent,
    Chip,
    Link,
    Stack,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { Theme, useTheme } from '@mui/material/styles';
import { themeVars } from '@mds/ui';
import { Link as RouterLink } from 'react-router-dom';
import { getChannelPublisherUrl } from '../../../utils/channel.utils';
import { ChannelSubscriptionButton } from './ChannelSubscriptionButton';
import { Channel, ChannelTypes } from '../../../shared/types';
import { ChannelCoverImage } from '../../ui/ChannelCoverImage';
import { normalizeImageUrl } from '../../../utils/image-url.utils';

interface ChannelCoverProps {
    channel: Channel;
    disclaimerText?: JSX.Element | string;
}

const level1Alpha = (theme: Theme, opacity: number) =>
    theme.alpha(themeVars(theme).palette.background.paper, opacity);

const channelTitleStripSx = (compact: boolean) => (theme: Theme) => ({
    zIndex: 1,
    position: 'relative',
    overflow: 'hidden',
    width: '100%',
    height: compact ? 124 : 104,
    background: `linear-gradient(180deg, ${level1Alpha(theme, 0.08)} 0%, ${level1Alpha(theme, 0.72)} 42%, ${level1Alpha(theme, 0.9)} 100%)`,
    p: compact ? 2 : 4,
});

const ChannelCover = ({ channel, disclaimerText }: ChannelCoverProps) => {
    const theme = useTheme();
    const xs = useMediaQuery(theme.breakpoints.down('sm'));
    const compact = xs;

    if (!channel) return <></>;

    return (
        <Card
            variant="outlined"
            id="channel-cover"
            sx={{ overflow: 'hidden', bgcolor: 'background.level1' }}
        >
            <Box
                sx={{
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'flex-end',
                    height: compact ? 124 : 250,
                    overflow: 'hidden',
                }}
            >
                <Box
                    component={ChannelCoverImage}
                    src={channel.coverImage}
                    alt=""
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                    }}
                />
                {channel.type === ChannelTypes.premium && (
                    <Box
                        sx={{
                            position: 'absolute',
                            zIndex: 2,
                            top: compact ? 16 : 32,
                            left: compact ? 16 : 32,
                        }}
                    >
                        <Chip label="Premium" color="primary" size="small" />
                    </Box>
                )}
                <Stack
                    id="channel-cover-title-strip"
                    direction="row"
                    spacing={4}
                    sx={[
                        {
                            alignItems: compact ? 'flex-end' : 'center',
                        },
                        channelTitleStripSx(compact),
                    ]}
                >
                    {!compact && channel.profileImage && (
                        <Avatar
                            src={normalizeImageUrl(channel.profileImage)}
                            alt=""
                            sx={{
                                width: 80,
                                height: 80,
                                border: (theme) =>
                                    `3px solid ${themeVars(theme).palette.background.paper}`,
                            }}
                        />
                    )}
                    <Stack
                        spacing={0.5}
                        sx={{
                            flex: 1,
                        }}
                    >
                        <Typography
                            variant={compact ? 'h5' : 'h3'}
                            sx={{
                                color: 'text.primary',
                            }}
                        >
                            {channel.name}
                        </Typography>
                        <Typography
                            variant={compact ? 'caption' : 'body1'}
                            sx={{
                                color: 'text.primary',
                            }}
                        >
                            Published by{' '}
                            <Link
                                component={RouterLink}
                                to={getChannelPublisherUrl(channel)}
                                underline="hover"
                            >
                                <strong>{channel.owner.name}</strong>
                            </Link>
                        </Typography>
                    </Stack>
                </Stack>
            </Box>
            <CardContent
                sx={{
                    display: 'flex',
                    flexDirection: compact ? 'column' : 'row',
                    justifyContent: 'space-between',
                    gap: 2,
                    bgcolor: 'background.level1',
                    p: compact ? 2 : 4,
                    '&:last-child': {
                        pb: compact ? 2 : 4,
                    },
                }}
            >
                <Box>
                    <Typography
                        variant={compact ? 'body1' : 'subtitle1'}
                        sx={{
                            color: 'text.secondary',
                            maxWidth: 800,
                        }}
                    >
                        {channel.description}
                    </Typography>
                    {channel.url && !compact && (
                        <Typography
                            variant="caption"
                            sx={{ display: 'block', mt: 1 }}
                        >
                            <Link
                                href={channel.url}
                                target="_blank"
                                rel="noreferrer"
                                underline="hover"
                            >
                                <strong>Learn more</strong>
                            </Link>
                        </Typography>
                    )}
                </Box>
                <ChannelSubscriptionButton
                    disclaimer={!compact}
                    disclaimerText={disclaimerText}
                    channel={channel}
                    size={compact ? 'small' : 'medium'}
                    align={compact ? 'left' : 'center'}
                />
            </CardContent>
        </Card>
    );
};

export default ChannelCover;
